import React, { useState, useEffect, useRef } from 'react';
import { Collapse, Typography, Space, Tag } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';

const { Panel } = Collapse;
const { Title, Text, Link } = Typography;

/**
 * SearchResults component for displaying vector search results
 * 
 * @param {Object} props
 * @param {Object} props.data - The data object containing search results
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 */
const SearchResults = ({
  data,
  title = "Search Results",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };


  //////////  Component data sync from backend ?????????????????
  const [updateCounter, setUpdateCounter] = useState(0);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);
  //////////  Component data sync from backend ?????????????????

  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;
  
  // Format the confidence score as a percentage tag with color
  const formatScoreTag = (score) => {
    let backgroundColor;
    let textColor = 'black';
    
    if (score >= 90) {
      backgroundColor = '#4CAF50'; // Deeper green
      textColor = 'white'; // White text for green backgrounds
    }
    else if (score >= 80) {
      backgroundColor = '#8BC34A'; // Slightly lighter green
      textColor = 'white'; // White text for green backgrounds
    }
    else if (score >= 70) backgroundColor = '#FFC107'; // Deeper amber/yellow
    else if (score >= 60) backgroundColor = '#FFEB3B'; // Slightly lighter yellow
    else backgroundColor = '#F44336'; // Red for lower scores
    
    return (
      <Tag 
        style={{ 
          float: 'right', 
          marginTop: '4px',
          backgroundColor: backgroundColor,
          color: textColor,
          border: 'none',
          fontWeight: 'bold'
        }}
      >
        {score}%
      </Tag>
    );
  };
  
  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px'   }}>
                {title}
              </div>
            ),
            children: (
              <div>
                {/* Search query */}
                {data && data.search_text && (
                  <Title level={4} style={{ marginBottom: '24px' }}>
                    Search results for: {data.search_text}
                  </Title>
                )}
                
                {/* Results list */}
                {data && data.results && data.results.length > 0 ? (
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {data.results.map((result, index) => (
                      <div key={index} style={{ marginBottom: '24px' }}>
                        <Text type="secondary" style={{ display: 'block', fontSize: '14px' }}>
                          {result.link}
                        </Text>
                        <Link href={result.link} target="_blank" style={{ fontSize: '18px', fontWeight: 'bold', color: '#0066cc', display: 'block' }}>
                          {result.title}
                        </Link>
                        {formatScoreTag(result.score)}
                        <Text style={{ display: 'block', marginTop: '8px' }}>
                          {result.snippet}
                        </Text>
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Text>No search results found.</Text>
                )}
                
                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <button 
                      size="small" 
                      icon={<LeftOutlined />}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <button 
                      size="small" 
                      icon={<RightOutlined />}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default SearchResults;